import Close from '@mui/icons-material/Close';
import { Button, IconButton, Link, Paper } from '@mui/material';
import { SOFTWARE_INTEGRATOR_EMAIL_ADDRESS } from '@softwareIntegrator/routes/public';
import clsx from 'clsx';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

export const ContactBox = ({
    sticky = false,
    onClickContact,
    onClose,
}: {
    sticky?: boolean;
    onClickContact?: () => void;
    onClose?: () => void;
    onCloseContact?: () => void;
}) => {
    const handleScrollToMap = useCallback(() => {
        const mapEl = document.getElementById('map');
        mapEl?.scrollIntoView({ behavior: 'smooth' });
        onClose?.();
    }, []);

    return (
        <Paper elevation={0} className={clsx('bg-neutral-650 p-4 rounded-xl', { 'sticky top-4 pt-8': sticky })}>
            {!sticky && (
                <div className="text-right">
                    <IconButton onClick={onClose} className="p-0">
                        <Close />
                    </IconButton>
                </div>
            )}
            <div className="flex flex-col gap-8">
                <div>
                    <p className="font-museo font-semibold text-2xl">
                        <FormattedMessage
                            id="softwareIntegrator.contactBox.discoverNetwork"
                            defaultMessage="Discover our network"
                        />
                    </p>
                    <p className="my-4">
                        <FormattedMessage
                            id="softwareIntegrator.contactBox.discoverNetwork.description"
                            defaultMessage="Browse our network of recommended software integrators on the <a>map above</a> 
                            to find the right specialist for your project."
                            values={{
                                a: (chunks) => (
                                    <Link className="cursor-pointer" onClick={handleScrollToMap} underline="none">
                                        {chunks}
                                    </Link>
                                ),
                            }}
                        />
                    </p>
                </div>
                <div>
                    <p className="font-museo font-semibold text-2xl">
                        <FormattedMessage
                            id="softwareIntegrator.contactBox.applyToJoin"
                            defaultMessage="Apply to join the network"
                        />
                    </p>
                    <p className="my-4">
                        <FormattedMessage
                            id="softwareIntegrator.contactBox.applyToJoin.descriptionUseButtonToContactUs"
                            defaultMessage="If you are a software developer with solid experience in Victron systems and want to join our network of software integrators, please use the button below to contact us."
                        />
                    </p>
                    <Button
                        className="rounded-full w-full"
                        component="a"
                        href={`mailto:${SOFTWARE_INTEGRATOR_EMAIL_ADDRESS}`}
                    >
                        <FormattedMessage id="common.sendUsAnEmail" defaultMessage="Send us an email" />
                    </Button>
                </div>
                <div>
                    <p className="font-museo font-semibold text-2xl">
                        <FormattedMessage
                            id="softwareIntegrator.contactBox.letsConnect"
                            defaultMessage="Let's Connect"
                        />
                    </p>
                    <p className="my-4">
                        <FormattedMessage
                            id="softwareIntegrator.contactBox.letsConnect.description"
                            defaultMessage="If you'd like more information or want to share how the Software Integrator Program has helped
                            you, please email us at <a>softwareintegrators@victronenergy.com</a>."
                            values={{
                                a: (chunks) => (
                                    <a
                                        href={`mailto:${SOFTWARE_INTEGRATOR_EMAIL_ADDRESS}`}
                                        target="_blank"
                                        className="text-spanish-blue"
                                    >
                                        {chunks}
                                    </a>
                                ),
                            }}
                        />
                    </p>

                    <p className="my-6">
                        <FormattedMessage
                            id="softwareIntegrator.contactBox.letsConnect.description2"
                            defaultMessage="We'd love to hear about your projects and see the innovative solutions you're creating with
                            Victron systems!"
                        />
                    </p>
                    <Button
                        onClick={() => {
                            onClickContact?.();
                            onClose?.();
                        }}
                        className="rounded-full w-full"
                    >
                        <FormattedMessage id="common.contactUs" defaultMessage="Contact us" />
                    </Button>
                </div>
            </div>
        </Paper>
    );
};
